import React, { useContext, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Icons,
  RadioGroup,
  SimpleDialog,
  Stack,
  TextArea,
  TextField,
} from '@nextiva/next-ui-components';
import { Controller } from 'react-hook-form-new';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { isValidUrl } from 'utils/validators';
import { BlueMessage, RadioLabel, StyledIcon } from '../Steps.styled';
import { useWizardContextV3 } from '../WizardContextV3/WizardContextV3';
import { CUSTOMER_CONSENT_FIELDS, FIELD_NAMES } from '../constants';
import { UploadComponent } from '../FileUpload';
import { FileTypesDialogSupportingDocuments } from '../FileTypesDialog';
import { Body2Typo, OverlineTypo, SubtitleTypo } from '../WizardV3.styled';

const dataTestId = 'PlatformAdminUI-CustomerConsent';
const placeHolderText =
  'Example: \n' +
  '“We gain all consent to initiate SMS with customers once they have the new patient phone call and agree verbally. While we do not have a set script, we let them know about the possible scheduling or follow-up SMS that we may send, that data rates might apply, frequency varies based on scheduling, and that they are welcome to ask for help or revoke their consent to SMS at any time. This will be retained in their file.”\n' +
  '\n' +
  ' “We collect consent on our website. The link to a webform is (give link to the webform, the webform must contain disclosures link), the types of the messages we will send, messaging and data rates, frequency, opt out and support keywords are described in the webform (give link to the webform). We store consent in our customer’s database.” \n' +
  '\n' +
  '“We collect consent via paper form (add details, include disclosures and attach photocopy of the form).”';

export function CustomerConsent() {
  const {
    control,
    formState: { errors },
    watch,
    filesSupportDocs,
  } = useWizardContextV3();
  const customerConsentForm = watch('customerConsentForm');
  const {
    featureState: { smsEnablementGenerateContent },
  } = useContext(FeatureContext);
  const [modalState, setModalState] = useState<boolean>(false);
  const [typeModal, setTypeModal] = useState<boolean>(false);
  const [writeOwnExplanation, setWriteOwnExplanation] = useState<boolean>(
    false,
  );
  const [customerInitiate, setCustomerInitiate] = useState<boolean>(false);
  const [consentVerbal, setConsentVerbal] = useState<boolean>(false);
  const [consentPaperForm, setConsentPaperForm] = useState<boolean>(false);
  const [consentOnlineApp, setConsentOnlineApp] = useState<boolean>(false);

  return (
    <div>
      <SubtitleTypo mb={2}>
        Please review and understand mandatory disclosures
      </SubtitleTypo>
      <Body2Typo>
        The SMS Disclosures should be complete and clear when your customer
        consents to receiving text messages from you. SMS disclosures should
        always have the following elements:
        <ul style={{ margin: '16px 0 48px 22px' }}>
          <li>Brand name.</li>
          <li>Messaging topic (The intended use for messaging).</li>
          <li>
            Message and data rate information (Example: Message and data rates
            may apply).
          </li>
          <li>
            Opt-out/opt-in and support information (Example: Reply STOP to
            opt-out of future messaging. Reply HELP for more information).
          </li>
          <li>
            Message frequency information (Example: Number of
            messages/month/week/etc., or message frequency varies, or recurring
            messages).
          </li>
          <li>
            Privacy policy and Terms & Conditions (A link to the privacy policy
            and Terms and Conditions, if there are any).
          </li>
        </ul>
        <Divider />
      </Body2Typo>
      <SubtitleTypo mt={4} mb={2}>
        Please explain how customer consent is obtained
        <IconButton
          aria-label="customer-consent-tooltip"
          onClick={() => setModalState(true)}
          data-testid={`${dataTestId}-Modal-HelpIcon`}
        >
          <StyledIcon icon={Icons.info} />
        </IconButton>
        <SimpleDialog
          open={modalState}
          title="Messaging flow"
          confirmText="Done"
          confirmButtonProps={{
            color: 'primary',
            onClick: () => setModalState(false),
            'data-testid': 'PlatformAdminUI-CustomerConsent-DoneButton',
          }}
        >
          <Body2Typo width="392px" mb={3}>
            Please describe how customers give their consent to text them and
            how you obtain their phone numbers (webform, verbally, phone calls,
            etc). Please include SMS Disclosure verbiage that you use: your
            Brand, what messages are you going to send, messaging and data
            rates, frequency, opt out and support keywords. Where do you store
            the data? (Patients file, CRM, customers account, etc)
          </Body2Typo>
        </SimpleDialog>
      </SubtitleTypo>
      {smsEnablementGenerateContent && (
        <>
          <RadioGroup
            aria-labelledby="obtained-label"
            id="obtain"
            data-testid={`${dataTestId}-Obtained`}
            onChange={(e) => setWriteOwnExplanation(e.target.value === 'true')}
            defaultValue="false"
          >
            <Stack direction="row" mb={2}>
              <RadioLabel
                label="Let us help create your explanation"
                value="false"
              />
              <BlueMessage variant="caption">Recommended</BlueMessage>
            </Stack>
            <RadioLabel
              label="Write your explanation without help"
              value="true"
            />
          </RadioGroup>

          <Body2Typo mb={1} mt={2}>
            Select all that applies
          </Body2Typo>
          <FormControlLabel
            control={
              <Checkbox
                disableRipple
                disabled={writeOwnExplanation}
                onChange={(event) => setCustomerInitiate(event.target.checked)}
                checked={customerInitiate}
                data-testid={`${dataTestId}-Conversation-Checkbox`}
              />
            }
            label={
              <Body2Typo ml={1} fontWeight="bold">
                Customer initiates conversation
              </Body2Typo>
            }
          />
          <Body2Typo mt={1} mb={2}>
            You can respond to the customer with related information; a general
            disclosure to obtain consent is not required. You cannot initiate or
            send texts that are unrelated to the initial inquiry you received
            from customers. You cannot send promotional or marketing
            information.
          </Body2Typo>
          <FormControlLabel
            control={
              <Checkbox
                disableRipple
                disabled={writeOwnExplanation}
                onChange={(event) => setConsentVerbal(event.target.checked)}
                checked={consentVerbal}
                data-testid={`${dataTestId}-Consent-Checkbox`}
              />
            }
            label={
              <Body2Typo ml={1} fontWeight="bold">
                Consent received verbally
              </Body2Typo>
            }
          />
          <Body2Typo mt={1} mb={2}>
            Your opt-in message must include mandatory disclosures.
          </Body2Typo>
          <FormControlLabel
            control={
              <Checkbox
                disableRipple
                onChange={(event) => setConsentPaperForm(event.target.checked)}
                checked={consentPaperForm}
                disabled={writeOwnExplanation}
                data-testid={`${dataTestId}-Paperform-Checkbox`}
              />
            }
            label={
              <Body2Typo ml={1} fontWeight="bold">
                Consent received in paper form
              </Body2Typo>
            }
          />
          <Body2Typo mt={1} mb={2}>
            Provide link or attach image of customer consent form. It must
            include mandatory disclosures.
          </Body2Typo>
          <FormControlLabel
            control={
              <Checkbox
                disableRipple
                disabled={writeOwnExplanation}
                onChange={(event) => setConsentOnlineApp(event.target.checked)}
                checked={consentOnlineApp}
                data-testid={`${dataTestId}-Application-Checkbox`}
              />
            }
            label={
              <Body2Typo ml={1} fontWeight="bold">
                Consent received through online application
              </Body2Typo>
            }
          />
          <Body2Typo mt={1} mb={4}>
            Provide link or attach image of customer consent form. It must
            include mandatory disclosures.
          </Body2Typo>
          <Controller
            control={control}
            name={CUSTOMER_CONSENT_FIELDS.CUSTOMER_CONSENT_FORM}
            rules={{ maxLength: 255 }}
            render={({ field: { onChange, onBlur, value = '' } }) => (
              <TextField
                id="customer-consent-form"
                data-testid={`${dataTestId}-CustomerConsentForm`}
                sx={{ width: 692 }}
                label={
                  <OverlineTypo>
                    provide link if you have an online customer consent form
                  </OverlineTypo>
                }
                disabled={writeOwnExplanation}
                helperText="Please provide correct URL format within 255 characters"
                placeholder="Enter URL"
                error={
                  // adding isValidUrl to rules makes this Textfield required, but we want this to be optional
                  !!customerConsentForm && !isValidUrl(customerConsentForm)
                }
                onChange={onChange}
                onBlur={onBlur}
                value={value}
              />
            )}
          />
          <Stack width="208px" height="40px" mt="16px">
            <Button
              disabled={
                writeOwnExplanation ||
                !(
                  customerInitiate ||
                  consentVerbal ||
                  consentPaperForm ||
                  consentOnlineApp
                ) ||
                !!errors.customerConsentForm
              }
              color="primary"
              variant="outlined"
            >
              Generate explanation
            </Button>
          </Stack>
        </>
      )}
      <Controller
        control={control}
        name={CUSTOMER_CONSENT_FIELDS.CUSTOMER_CONSENT}
        rules={{
          required: true,
          minLength: { value: 40, message: 'Minimum 40 characters' },
          maxLength: { value: 4000, message: 'Maximum 4000 characters' },
        }}
        render={({
          field: { onChange, onBlur, value = '' },
          fieldState: { error },
        }) => {
          return (
            <TextArea
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              charLimit={4000}
              maxRows={5}
              required
              sx={{ width: '495px', mt: '16px' }}
              error={!!error}
              label={
                <OverlineTypo>
                  {FIELD_NAMES[CUSTOMER_CONSENT_FIELDS.CUSTOMER_CONSENT]}
                </OverlineTypo>
              }
              placeholder={placeHolderText}
              helperText={error?.message || 'Minimum 40 characters'}
              data-testid={`${dataTestId}-ExplainConsent-Textarea`}
            />
          );
        }}
      />
      <SubtitleTypo mt={4} mb={2}>
        Provide necessary documents to support your explanation
        <IconButton
          aria-label="customer-document-tooltip"
          onClick={() => setTypeModal(true)}
          data-testid={`${dataTestId}-Document-HelpIcon`}
        >
          <StyledIcon icon={Icons.info} />
        </IconButton>
      </SubtitleTypo>
      <FileTypesDialogSupportingDocuments
        open={typeModal}
        setOpen={setTypeModal}
      />
      <OverlineTypo>UPLOAD DOCUMENTS</OverlineTypo>
      <UploadComponent
        onFileFormats={setTypeModal}
        uploadedFiles={filesSupportDocs}
        attachmentType="supportingDocument"
      />
    </div>
  );
}
